import { useGetNotificationByAcl } from 'api-hooks/notification/query';
import { useGetBalanceAdjustments } from 'api-hooks/transactions/balance-adjustment';
import { AuthorizationRules, NavigationRoutes } from 'common/constants';
import { capitalize, string2money } from 'common/utils/string';
import notification from 'components/common/notification';
import QueryFilter from 'components/common/query-filter/query-filter';
import ListHeader from 'components/elements/list-header';
import { Container, TopContainer } from 'components/styles';
import { useApplyQueryFilter } from 'hooks/filter/use-apply-query-filter';
import { FilterHandleParentProvider } from 'hooks/filter/use-filter-handle-parent';
import useApplyQuerySort from 'hooks/use-apply-query-sort';
import useComposedQuery from 'hooks/use-composed-query';
import useDate from 'hooks/use-date';
import useGetCrudPermission from 'hooks/use-get-crud-permission';
import useGetListParamsValue from 'hooks/use-get-list-params-value';
import useStatusFilter from 'hooks/use-status-filter';
import { PillStatus } from 'legacy-modules/styles';
import useTranslation from 'next-translate/useTranslation';
import React from 'react';

import BalanceAdjustmentTable from './components/table';
import TransactionPillStatus from '../component/status';
import TransactionListTabs from '../component/transaction-list-tab';

export default function BalanceAdjustmentsList() {
  const { t } = useTranslation();
  const { queryStatus, queryPage, queryTab } = useGetListParamsValue();
  const [page, setPage] = React.useState(queryPage);
  const [activeKey, setActiveKey] = React.useState<React.Key>(queryTab);
  const [status, setStatus] = useStatusFilter<string | undefined>(
    queryStatus,
    setPage,
  );

  const formatDate = useDate();

  const _columns = React.useMemo(
    () => [
      {
        header: t('common:status'),
        accessorKey: 'status',
        minSize: 150,
        cell: ({ getValue }) => <TransactionPillStatus value={getValue()} />,
      },
      {
        header: t('common:date'),
        accessorKey: 'date',
        minSize: 150,
        sortName: 'date',
        cell: ({ getValue }) => {
          return <>{formatDate(getValue() as Date, 'dd MMM yyyy')}</>;
        },
      },
      {
        header: t('common:number'),
        accessorKey: 'number',
        minSize: 150,
      },
      {
        header: t('common:user_type'),
        accessorKey: 'balanceableType',
        minSize: 150,
        sortName: 'balanceable_type',
        cell: ({ getValue }) => {
          return <>{capitalize(getValue() as string)}</>;
        },
      },
      {
        header: t('common:name'),
        accessorKey: 'balanceableName',
        minSize: 150,
      },
      {
        header: t('common:type'),
        accessorKey: 'type',
        minSize: 150,
        sortName: 'type',
        cell: ({ getValue }) => {
          return (
            <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
              <PillStatus status={getValue() === 'add' ? 'green' : 'red'}>
                {getValue() === 'add' ? t('common:add') : t('common:subtract')}
              </PillStatus>
            </div>
          );
        },
      },
      {
        header: t('common:amount'),
        accessorKey: 'amount',
        minSize: 150,
        textAlign: 'right',
        cell: ({ getValue }) => string2money(getValue() as number),
      },
      {
        header: t('common:description'),
        accessorKey: 'description',
        minSize: 200,
        sortName: 'description',
      },
      {
        header: t('common:created_by'),
        accessorKey: 'createdBy.name',
        minSize: 150,
        sortName: 'created_by',
        cell: ({ getValue }) => <>{getValue() as string}</>,
      },
    ],
    [formatDate, t],
  );

  const {
    data,
    refetch,
    isLoading,
    isFetching,
    error,
    query,
    extras: [{ filters, setFilters }, { columns }],
  } = useComposedQuery(
    useGetBalanceAdjustments,
    {
      params: {
        page,
        filter: { status },
      },
    },
    useApplyQueryFilter((data: any) => {
      return data.filters;
    }),
    useApplyQuerySort((data: any) => {
      return data.sorts;
    }, _columns),
  );

  const { data: dataNotification, refetch: refetchNotification } =
    useGetNotificationByAcl({
      acl: AuthorizationRules.BalanceAdjustmentsPage,
    });

  React.useEffect(() => {
    if (!!error && !!error.message) {
      notification.error({ message: error.message });
    }
  }, [error]);
  const permission = useGetCrudPermission('balance-adjustments');
  return (
    <FilterHandleParentProvider
      values={{
        filters,
        setFilters,
        page,
        setPage,
      }}
    >
      <Container>
        <ListHeader
          query={query}
          title={t('common:balance_adjustment')}
          showAdd={permission.create}
          onRefresh={() => {
            refetch();
            refetchNotification();
          }}
          linkAdd={NavigationRoutes.BalanceAdjustmentsCreate}
        />

        <TransactionListTabs
          {...{
            activeKey,
            setActiveKey: (value) => {
              setActiveKey(value);
              setFilters();
            },
            setStatus,
            badge: dataNotification?.data,
          }}
        />
        <TopContainer>
          <QueryFilter filters={filters} isUseResponseOptions />
        </TopContainer>
        <BalanceAdjustmentTable
          balanceAdjustment={data?.data || []}
          loading={isLoading || isFetching}
          page={page}
          onPageChange={(page) => setPage(page)}
          meta={data?.meta}
          sortColumns={columns || []}
        />
      </Container>
    </FilterHandleParentProvider>
  );
}
